import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ApplicationTypeOptions } from '../../components/ApplicationsTable/components/ApplicationsFilter';
import { useApplicationsContext } from '.';
import { useUnderwritersContext } from '../underwriters';

type ApplicationsUIContextType = {
  refreshData: () => Promise<void>;
  key: number;
  loading: boolean;
  lastRetrieved: Date;
  filterStatus: string[],
  filterType: keyof typeof ApplicationTypeOptions,
  onlyUpdatedApplications: boolean,
  filterSectionHeight: number,
  setLastRetrieved: Dispatch<SetStateAction<Date>>;
  setFilterStatus: Dispatch<SetStateAction<string[]>>;
  setFilterType: Dispatch<SetStateAction<keyof typeof ApplicationTypeOptions>>;
  setOnlyUpdatedApplications: Dispatch<SetStateAction<boolean>>;
  setFilterSectionHeight: Dispatch<SetStateAction<number>>;
}

const defaultApplicationsContext: ApplicationsUIContextType = {
  refreshData: async () => { },
  key: 0,
  loading: false,
  lastRetrieved: new Date(),
  filterStatus: [],
  filterType: 'ALL',
  onlyUpdatedApplications: false,
  filterSectionHeight: 0,
  setLastRetrieved: () => { },
  setFilterStatus: () => { },
  setFilterType: () => { },
  setOnlyUpdatedApplications: () => { },
  setFilterSectionHeight: () => { },
};

const ApplicationsUIContext = createContext<ApplicationsUIContextType>(defaultApplicationsContext);

export function useApplicationsUIContext() {
  return useContext(ApplicationsUIContext);
}

export function ApplicationsUIProvider({ children }: PropsWithChildren) {
  const { fetchApplications } = useApplicationsContext();
  const { fetchUnderwriters } = useUnderwritersContext();
  const [lastRetrieved, setLastRetrieved] = useState(new Date());
  const [key, setKey] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<keyof typeof ApplicationTypeOptions>('ALL');
  const [filterSectionHeight, setFilterSectionHeight] = useState<number>(0);
  const [onlyUpdatedApplications, setOnlyUpdatedApplications] = useState(false);

  const refreshData = async () => {
    setLastRetrieved(new Date());
    setLoading(true);
    setKey((prev) => prev + 1);
    setOnlyUpdatedApplications(false);
    setFilterStatus([]);
    try {
      await Promise.all([fetchApplications(), fetchUnderwriters()]);
    } finally {
      setLoading(false);
    }
  };

  const contextValue = useMemo(() => ({
    refreshData,
    key,
    loading,
    filterStatus,
    lastRetrieved,
    filterType,
    onlyUpdatedApplications,
    filterSectionHeight,
    setLastRetrieved,
    setFilterStatus,
    setFilterType,
    setOnlyUpdatedApplications,
    setFilterSectionHeight,
  }), [
    key,
    loading,
    lastRetrieved,
    filterStatus,
    filterType,
    onlyUpdatedApplications,
    filterSectionHeight,
  ]);

  return (
    <ApplicationsUIContext.Provider value={contextValue}>
      {children}
    </ApplicationsUIContext.Provider>
  );
}
